import * as React from 'react';
import { Box, Breadcrumbs, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';
import ProjectLogo from '../../components/commons/ProjectLogo';
import { projectConfig } from '../../config/projectConfig';

type BreadcrumbItem = {
  label: string;
  to?: string;
};

type ContentLayoutProps = {
  children: React.ReactNode;
  title: string;
  showLogo?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  withBackground?: boolean;
  breadcrumbs?: BreadcrumbItem[];
};

export const ContentLayout = ({
  children,
  title,
  showLogo = true,
  maxWidth = 'lg',
  withBackground = false,
  breadcrumbs = [],
}: ContentLayoutProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        position: 'relative',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Optional subtle background image */}
      {withBackground && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${projectConfig.bgLogin})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.03,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 2, sm: 4 },
        }}
      >
        <Container
          maxWidth={maxWidth}
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{
                mb: { xs: 1.5, sm: 2.5 },
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'wrap',
                },
              }}
            >
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1 || !item.to;
                if (isLast) {
                  return (
                    <Typography
                      key={`${item.label}-${index}`}
                      variant="body2"
                      sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                      {item.label}
                    </Typography>
                  );
                }

                return (
                  <Box
                    key={`${item.label}-${index}`}
                    component={RouterLink}
                    to={item.to as string}
                    sx={{
                      color: 'text.secondary',
                      typography: 'body2',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {item.label}
                  </Box>
                );
              })}
            </Breadcrumbs>
          )}

          {/* Header with Logo and Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: { xs: 2, sm: 4 },
              flexWrap: 'wrap',
            }}
          >
            {showLogo && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    theme.palette.mode === 'dark' ? 'rgba(35, 31, 26, 0.94)' : 'rgba(255, 255, 255, 0.95)',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  padding: 1.5,
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? '0 18px 34px rgba(12, 9, 7, 0.4)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  minWidth: 64,
                  minHeight: 64,
                }}
              >
                <ProjectLogo size="header" />
              </Box>
            )}
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              paddingTop: { xs: 1, sm: 2 },
              paddingBottom: { xs: 4, sm: 8 },
            }}
          >
            {children}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
